import { Calendar } from "lucide-react";
import { useState, useEffect } from "react";
import Chart from "react-apexcharts";
import BONavigation from "../../../components/admin/BONavigation";
import {
    getEstablishmentStatusStats,
    EstablishmentStatusStats,
    checkSirenStatus,
    SirenCheckResult,
} from "../../../interfaces/establishments";

function formatDate(dateStr: string | null | undefined): string {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
}

function formatTime(dateStr: string | null | undefined): string {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    const today = new Date();
    const isToday =
        d.getDate() === today.getDate() &&
        d.getMonth() === today.getMonth() &&
        d.getFullYear() === today.getFullYear();
    if (isToday) {
        return `Aujourd'hui, ${d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}`;
    }
    return `${d.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}, ${d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}`;
}

function daysAgo(dateStr: string | null | undefined): string {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    const diff = Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
    if (diff === 0) return "aujourd'hui";
    return `il y a ${diff} j`;
}

function StatCard({
    label,
    value,
    sub,
    valueColor,
    icon: Icon,
}: {
    label: string;
    value: number | string;
    sub?: string;
    valueColor?: string;
    icon?: React.ComponentType<{ className?: string }>;
}) {
    return (
        <div className="border border-gray-200 rounded-lg p-4">
            <p className="text-sm text-gray-500 mb-1">{label}</p>
            <p className={`text-2xl font-semibold ${valueColor ?? "text-gray-900"}`}>
                {typeof value === "number" ? value.toLocaleString("fr-FR") : value}
            </p>
            {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
        </div>
    );
}

function VerifCard({
    title,
    info,
    icon: Icon,
}: {
    title: string;
    info: EstablishmentStatusStats["mostRecentVerification"];
    icon: React.ComponentType<{ className?: string }>;
}) {
    return (
        <div className="border border-gray-200 rounded-lg p-4 flex items-start gap-4">
            <div className="flex-shrink-0 w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                <Icon className="w-5 h-5 text-gray-600" />
            </div>
            <div>
                <p className="text-xs text-gray-500 mb-0.5">{title}</p>
                <p className="text-base font-semibold text-gray-900">
                    {info ? formatTime(info.date) : "—"}
                </p>
                {info && (
                    <p className="text-xs text-gray-400 mt-0.5">
                        {info.name}
                        {info.location ? ` · ${info.location}` : ""}
                        {info.date ? ` · ${daysAgo(info.date)}` : ""}
                    </p>
                )}
            </div>
        </div>
    );
}

const STATUS_LABELS: Record<string, string> = {
    active: "Actif",
    closed: "Fermé",
    in_liquidation: "En liquidation",
    other: "Autre",
};

const STATUS_COLORS: Record<string, string> = {
    active: "text-green-600 bg-green-50 border-green-200",
    closed: "text-red-600 bg-red-50 border-red-200",
    in_liquidation: "text-amber-600 bg-amber-50 border-amber-200",
    other: "text-gray-600 bg-gray-50 border-gray-200",
};

function StatusBadge({ status }: { status: string | null }) {
    if (!status) return <span className="text-gray-400 text-xs">—</span>;
    const cls = STATUS_COLORS[status] ?? STATUS_COLORS.other;
    return (
        <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded border ${cls}`}>
            {STATUS_LABELS[status] ?? status}
        </span>
    );
}

type Tab = "pipeline" | "quality";

export default function StatusMonitoring() {
    const [stats, setStats] = useState<EstablishmentStatusStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<Tab>("pipeline");

    const [siren, setSiren] = useState("999962806");
    const [checking, setChecking] = useState(false);
    const [checkResult, setCheckResult] = useState<SirenCheckResult | null>(null);
    const [checkError, setCheckError] = useState<string | null>(null);

    useEffect(() => {
        getEstablishmentStatusStats()
            .then(setStats)
            .finally(() => setLoading(false));
    }, []);

    const handleCheckSiren = async () => {
        if (!siren.trim()) return;
        setChecking(true);
        setCheckResult(null);
        setCheckError(null);
        try {
            const result = await checkSirenStatus(siren.trim());
            setCheckResult(result);
        } catch (e: any) {
            setCheckError(e?.response?.data?.error ?? "Erreur lors de la vérification");
        } finally {
            setChecking(false);
        }
    };

    const labels = stats?.weeklyData.map((w) => w.label) ?? [];
    const verifiedSeries = stats?.weeklyData.map((w) => w.verified) ?? [];
    const closedSeries = stats?.weeklyData.map((w) => w.closed) ?? [];
    const createdSeries = stats?.weeklyData.map((w) => w.created) ?? [];
    const enrichedSeries = stats?.weeklyData.map((w) => w.enriched) ?? [];
    const _googlePlaceDoneSeries = stats?.weeklyData.map((w) => w.googlePlaceDone) ?? [];
    const _googlePlacePendingSeries = stats?.weeklyData.map((w) => w.googlePlacePending) ?? [];
    const _linkedinPendingSeries = stats?.weeklyData.map((w) => w.linkedinPending) ?? [];

    const barOptions = (color: string): ApexCharts.ApexOptions => ({
        chart: {
            type: "bar",
            toolbar: { show: false },
            fontFamily: "inherit",
            background: "transparent",
        },
        plotOptions: {
            bar: { borderRadius: 3, columnWidth: "55%" },
        },
        colors: [color],
        dataLabels: { enabled: false },
        xaxis: {
            categories: labels,
            labels: { style: { colors: "#6b7280", fontSize: "11px" } },
            axisBorder: { show: false },
            axisTicks: { show: false },
        },
        yaxis: {
            labels: { style: { colors: "#6b7280", fontSize: "11px" } },
        },
        grid: {
            borderColor: "#f3f4f6",
            strokeDashArray: 3,
            xaxis: { lines: { show: false } },
        },
        tooltip: {
            theme: "light",
            y: { formatter: (v: number) => v.toLocaleString("fr-FR") },
        },
        legend: {
            show: true,
            position: "top",
            horizontalAlign: "left",
            markers: { width: 8, height: 8 },
            labels: { colors: "#374151" },
            fontSize: "12px",
        },
    });

    const verifiedThisWeekSub = stats?.verifiedThisWeekChange != null
        ? `${stats.verifiedThisWeekChange >= 0 ? "+" : ""}${stats.verifiedThisWeekChange}% vs sem. préc.`
        : "cette semaine";

    return (
        <div className="min-h-screen bg-[#F5F0EB]">
            <div className="mx-auto max-w-7xl px-6 py-6">
                <BONavigation />

                <h1 className="text-xl font-semibold text-gray-900 mb-6">
                    Stats
                </h1>

                {loading ? (
                    <div className="text-sm text-gray-400">Chargement…</div>
                ) : !stats ? (
                    <div className="text-sm text-red-500">Erreur lors du chargement.</div>
                ) : (
                    <>
                        {/* Stats */}
                        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
                            <StatCard
                                label="Vérifiés cette semaine"
                                value={stats.verifiedThisWeek}
                                sub={verifiedThisWeekSub}
                            />
                            <StatCard
                                label="Fermés détectés"
                                value={stats.closedDetectedThisWeek}
                                sub="cette semaine"
                                valueColor="text-amber-600"
                            />
                            <StatCard
                                label="Non vérifiés +7 j"
                                value={stats.notVerifiedOver7Days}
                                sub="à traiter en priorité"
                                valueColor={stats.notVerifiedOver7Days > 0 ? "text-red-500" : "text-gray-900"}
                            />
                            <StatCard
                                label="Introuvables INSEE"
                                value={stats.inseeNotFound}
                                sub="SIREN 404 · déprioritisés"
                                valueColor={stats.inseeNotFound > 0 ? "text-orange-500" : "text-gray-900"}
                            />
                            <StatCard
                                label="Total actifs suivis"
                                value={stats.totalActiveTracked}
                                sub="dans la base"
                            />
                        </div>

                        {/* Verification cards */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                            <VerifCard
                                title="Vérification la plus récente"
                                info={stats.mostRecentVerification}
                                icon={Calendar}
                            />
                            <VerifCard
                                title="Vérification la plus ancienne"
                                info={stats.oldestVerification}
                                icon={Calendar}
                            />
                        </div>

                        {/* Tabs */}
                        <div className="flex gap-1 mb-4 border-b border-gray-200">
                            {([
                                { key: "pipeline", label: "Pipeline" },
                                { key: "quality", label: "Qualité / Alertes" },
                            ] as { key: Tab; label: string }[]).map((tab) => (
                                <button
                                    key={tab.key}
                                    onClick={() => setActiveTab(tab.key)}
                                    className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                                        activeTab === tab.key
                                            ? "border-gray-900 text-gray-900"
                                            : "border-transparent text-gray-500 hover:text-gray-700"
                                    }`}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </div>

                        {activeTab === "pipeline" && (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                <div className="border border-gray-200 rounded-lg p-4">
                                    <p className="text-sm font-semibold text-gray-900 mb-0.5">
                                        Établissements créés par semaine
                                    </p>
                                    <p className="text-xs text-gray-400 mb-4">8 dernières semaines</p>
                                    <div className="h-56">
                                        <Chart
                                            type="bar"
                                            width="100%"
                                            height="100%"
                                            options={barOptions("#6366f1")}
                                            series={[{ name: "Créés", data: createdSeries }]}
                                        />
                                    </div>
                                </div>

                                <div className="border border-gray-200 rounded-lg p-4">
                                    <p className="text-sm font-semibold text-gray-900 mb-0.5">
                                        Établissements vérifiés par semaine
                                    </p>
                                    <p className="text-xs text-gray-400 mb-4">8 dernières semaines</p>
                                    <div className="h-56">
                                        <Chart
                                            type="bar"
                                            width="100%"
                                            height="100%"
                                            options={barOptions("#3b82f6")}
                                            series={[{ name: "Vérifiés", data: verifiedSeries }]}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === "quality" && (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                <div className="border border-gray-200 rounded-lg p-4">
                                    <p className="text-sm font-semibold text-gray-900 mb-0.5">
                                        Enrichissements approuvés par semaine
                                    </p>
                                    <p className="text-xs text-gray-400 mb-4">8 dernières semaines · events avec enrichissement validé</p>
                                    <div className="h-56">
                                        <Chart
                                            type="bar"
                                            width="100%"
                                            height="100%"
                                            options={barOptions("#10b981")}
                                            series={[{ name: "Enrichis", data: enrichedSeries }]}
                                        />
                                    </div>
                                </div>

                                <div className="border border-gray-200 rounded-lg p-4">
                                    <p className="text-sm font-semibold text-gray-900 mb-0.5">
                                        Établissements fermés détectés par semaine
                                    </p>
                                    <p className="text-xs text-gray-400 mb-4">8 dernières semaines</p>
                                    <div className="h-56">
                                        <Chart
                                            type="bar"
                                            width="100%"
                                            height="100%"
                                            options={barOptions("#ef4444")}
                                            series={[{ name: "Fermés", data: closedSeries }]}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* SIREN tester */}
                        <div className="mt-4 border border-gray-200 rounded-lg p-4">
                            <p className="text-sm font-semibold text-gray-900 mb-3">
                                Tester une vérification INSEE
                            </p>
                            <div className="flex items-center gap-2 mb-4">
                                <input
                                    type="text"
                                    value={siren}
                                    onChange={(e) => setSiren(e.target.value)}
                                    placeholder="SIREN (9 chiffres)"
                                    className="border border-gray-200 rounded px-3 py-1.5 text-sm w-44 focus:outline-none focus:ring-1 focus:ring-gray-300"
                                />
                                <button
                                    onClick={handleCheckSiren}
                                    disabled={checking || !siren.trim()}
                                    className="bg-gray-900 text-white text-sm px-4 py-1.5 rounded hover:bg-gray-800 disabled:opacity-50"
                                >
                                    {checking ? "Vérification…" : "Lancer"}
                                </button>
                            </div>

                            {checkError && (
                                <p className="text-sm text-red-500">{checkError}</p>
                            )}

                            {checkResult && (
                                <div className="text-sm space-y-3">
                                    <p className="font-medium text-gray-900">
                                        {checkResult.establishment.name}
                                        <span className="ml-2 text-xs text-gray-400 font-normal">
                                            {checkResult.establishment._id}
                                        </span>
                                    </p>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="border border-gray-200 rounded p-3">
                                            <p className="text-xs text-gray-400 mb-2 uppercase tracking-wide">Avant</p>
                                            <div className="space-y-1.5">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xs text-gray-500 w-28">Statut</span>
                                                    <StatusBadge status={checkResult.before.status} />
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xs text-gray-500 w-28">Actif</span>
                                                    <span className="text-xs text-gray-700">
                                                        {checkResult.before.is_active === null ? "—" : checkResult.before.is_active ? "Oui" : "Non"}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xs text-gray-500 w-28">Dernière vérif.</span>
                                                    <span className="text-xs text-gray-700">
                                                        {checkResult.before.last_status_update ? formatDate(checkResult.before.last_status_update) : "Jamais"}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xs text-gray-500 w-28">INSEE</span>
                                                    {checkResult.before.insee_not_found
                                                        ? <span className="text-xs font-medium text-orange-600 bg-orange-50 border border-orange-200 px-2 py-0.5 rounded">Introuvable</span>
                                                        : <span className="text-xs text-gray-400">—</span>
                                                    }
                                                </div>
                                            </div>
                                        </div>
                                        <div className="border border-gray-200 rounded p-3">
                                            <p className="text-xs text-gray-400 mb-2 uppercase tracking-wide">
                                                Après
                                                {checkResult.changed && (
                                                    <span className="ml-2 text-amber-600 font-semibold normal-case">
                                                        · Statut modifié
                                                    </span>
                                                )}
                                            </p>
                                            <div className="space-y-1.5">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xs text-gray-500 w-28">Statut</span>
                                                    <StatusBadge status={checkResult.after.status} />
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xs text-gray-500 w-28">Actif</span>
                                                    <span className="text-xs text-gray-700">
                                                        {checkResult.after.is_active === null ? "—" : checkResult.after.is_active ? "Oui" : "Non"}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xs text-gray-500 w-28">Dernière vérif.</span>
                                                    <span className="text-xs text-gray-700">
                                                        {checkResult.after.last_status_update ? formatDate(checkResult.after.last_status_update) : "—"}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xs text-gray-500 w-28">INSEE</span>
                                                    {checkResult.after.insee_not_found
                                                        ? <span className="text-xs font-medium text-orange-600 bg-orange-50 border border-orange-200 px-2 py-0.5 rounded">Introuvable</span>
                                                        : <span className="text-xs text-gray-400">—</span>
                                                    }
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
